import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import FairPriceView from '@/components/polymer/FairPriceView';
import InventoryChangeView from '@/components/polymer/InventoryChangeView';
import RatesView from '@/components/polymer/RatesView';
import UsitcChart from '@/components/polymer/UsitcChart';
import FeedstocksTab from '@/components/polymer/FeedstocksTab';
import DemandProductionCapacityChart from '@/components/charts/DemandProductionCapacityChart';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import FreeTrialFade from '@/components/common/FreeTrialFade';
import PolymerTotalsView from '@/components/polymer/PolymerTotalsView';
import StackedBarChart from '@/components/charts/StackedBarChart';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { getCachedACCData, getPSInventoryMetrics } from '@/services/marketIntelService';
import { useTheme } from '@/contexts/ThemeContext';
import { chartColors } from '@/utils/chartColors';

const DEFAULT_DATE_FROM = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().split('T')[0];
})();
const DEFAULT_DATE_TO = new Date().toISOString().split('T')[0];

const PS_CONFIG: PolymerDashboardConfig = {
  polymer: 'PS',
  polymerFullName: 'Polystyrene',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: 'fair-price', label: 'Fair Resin Price' },
    { key: 'totals', label: 'Totals' },
    { key: 'inventory-change', label: 'Inventory Change' },
    { key: 'demand-production', label: 'Demand/Production/Capacity' },
    { key: 'feedstocks', label: 'Feedstocks' },
    { key: 'usitc', label: 'USITC' },
    { key: 'rates', label: 'Rates' },
    { key: 'feedstocks-usitc', label: 'Feedstocks USITC' },
  ],
};

const ChartPanel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
    <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 whitespace-nowrap">{title}</h3>
    {children}
  </div>
);

const PSDashboard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isDark } = useTheme();
  const [accData, setAccData] = useState<any[]>([]);
  const [inventoryMetrics, setInventoryMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeedstock, setSelectedFeedstock] = useState<'benzene' | 'butadiene' | 'styrene'>('benzene');
  const hasFetched = useRef(false);
  const [isSmallDpcSelector, setIsSmallDpcSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [hideChartLabels, setHideChartLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1299px)').matches,
  );
  const [isSmallFeedstocksUsitc, setIsSmallFeedstocksUsitc] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const chartLabelsMq = window.matchMedia('(max-width: 1299px)');
    const feedstocksUsitcMq = window.matchMedia('(max-width: 750px)');
    const overviewMq = window.matchMedia('(max-width: 750px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallDpcSelector(e.matches);
    const chartLabelsHandler = (e: MediaQueryListEvent) => setHideChartLabels(e.matches);
    const feedstocksUsitcHandler = (e: MediaQueryListEvent) => setIsSmallFeedstocksUsitc(e.matches);
    const overviewHandler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    mq.addEventListener('change', handler);
    chartLabelsMq.addEventListener('change', chartLabelsHandler);
    feedstocksUsitcMq.addEventListener('change', feedstocksUsitcHandler);
    overviewMq.addEventListener('change', overviewHandler);
    return () => {
      mq.removeEventListener('change', handler);
      chartLabelsMq.removeEventListener('change', chartLabelsHandler);
      feedstocksUsitcMq.removeEventListener('change', feedstocksUsitcHandler);
      overviewMq.removeEventListener('change', overviewHandler);
    };
  }, []);

  useEffect(() => {
    if (hasFetched.current || !isAuthenticated) return;
    hasFetched.current = true;
    setLoading(true);
    Promise.all([
      getCachedACCData('ps')
        .then(data => setAccData(data))
        .catch(() => setAccData([])),
      getPSInventoryMetrics()
        .then(data => setInventoryMetrics(data))
        .catch(() => setInventoryMetrics([])),
    ]).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const sorted = keepFirstEntryPerMonth(accData);
  const prodLabels = sorted.map((d) => String(d.date || d.record_date).slice(0, 10));

  // Demand/Production/Capacity reads the import tables; the feedstocks-USITC
  // charts stay on accData, which is the only source for those columns.
  const invMonthly = keepFirstEntryPerMonth(
    [...inventoryMetrics].sort((a, b) =>
      (a.date || a.record_date) < (b.date || b.record_date) ? -1 : 1
    )
  );
  const invLabels = invMonthly.map((d) => String(d.date || d.record_date).slice(0, 10));


  const renderTabContent = (activeTab: string) => {
    if (activeTab === 'overview') {
      return (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {isSmallOverview ? (
              <FairPriceView polymer="PS" dateFrom={DEFAULT_DATE_FROM} dateTo={DEFAULT_DATE_TO} labelMode="overview" fullBleed />
            ) : (
              <ChartPanel title="Fair Resin Price" className="flex flex-col">
                <FairPriceView polymer="PS" dateFrom={DEFAULT_DATE_FROM} dateTo={DEFAULT_DATE_TO} labelMode="overview" />
              </ChartPanel>
            )}
            {isSmallOverview ? (
              <div className="-mx-6">
                <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                  Demand, Production & Capacity
                </h3>
                <div className="h-[400px] px-2">
                  <DemandProductionCapacityChart
                    production={invMonthly.map(d => d.domestic_production ?? null)}
                    demand={invMonthly.map(d => d.total_sales ?? null)}
                    capacity={invMonthly.map(d => d.capacity ?? null)}
                    labels={invLabels}
                    polymer="PS"
                    showDataLabels={false}
                    isOverviewPage={true}
                  />
                </div>
              </div>
            ) : (
              <ChartPanel title="Demand, Production & Capacity" className="flex flex-col">
                <div className="flex-1 flex flex-col min-h-0">
                  <DemandProductionCapacityChart
                    production={invMonthly.map(d => d.domestic_production ?? null)}
                    demand={invMonthly.map(d => d.total_sales ?? null)}
                    capacity={invMonthly.map(d => d.capacity ?? null)}
                    labels={invLabels}
                    polymer="PS"
                    showDataLabels={false}
                    showBorder
                    isOverviewPage={true}
                  />
                </div>
              </ChartPanel>
            )}
          </div>
          {isSmallOverview ? (
            <FeedstocksTab polymer="PS" fullBleed />
          ) : (
            <ChartPanel title="Feedstocks">
              <FeedstocksTab polymer="PS" />
            </ChartPanel>
          )}
        </div>
      );
    }

    switch (activeTab) {
      case 'fair-price':
        return <FairPriceView polymer="PS" dateFrom={DEFAULT_DATE_FROM} dateTo={DEFAULT_DATE_TO} labelMode="tab" fullBleed />;
      case 'inventory-change':
        return (
          <InventoryChangeView
            data={inventoryMetrics}
            inventoryChangeKey="inventory_change_monthly"
            inventoryChangeCumulativeKey="inventory_change_cumulative"
            polymer="PS"
            fullBleed
          />
        );
      case 'demand-production': {
        const productionData = invMonthly.map(d => d.domestic_production ?? null);
        const demandData = invMonthly.map(d => d.total_sales ?? null);
        const capacityData = invMonthly.map(d => d.capacity ?? null);
        return (
          <div className={`flex flex-col gap-6 ${isSmallDpcSelector ? 'p-0 -mx-6' : 'p-4'}`}>
            <div className={`border border-rs-jet-light rounded-lg ${isSmallDpcSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallDpcSelector ? 'px-2 text-center' : ''}`}>Demand, Production & Capacity</h3>
              <div className="h-[700px]">
                <DemandProductionCapacityChart
                  production={productionData}
                  demand={demandData}
                  capacity={capacityData}
                  labels={invLabels}
                  polymer="PS"
                  showDataLabels={!hideChartLabels}
                />
              </div>
            </div>
          </div>
        );
      }
      case 'feedstocks':
        return <FeedstocksTab polymer="PS" fullBleed />;
      case 'usitc':
        return <UsitcChart polymer="PS" dateFrom={DEFAULT_DATE_FROM} dateTo={DEFAULT_DATE_TO} />;
      case 'rates':
        return <RatesView polymer="PS" />;
      case 'totals':
        return <PolymerTotalsView data={inventoryMetrics} polymer="PS" fullBleed />;
      case 'feedstocks-usitc': {
        const regions = [
          { suffix: 'canada', label: 'Canada' },
          { suffix: 'china', label: 'China' },
          { suffix: 'europe', label: 'Europe' },
          { suffix: 'latin_america', label: 'Latin America' },
          { suffix: 'mexico', label: 'Mexico' },
          { suffix: 'other', label: 'Other' },
          { suffix: 'pacific_rim', label: 'Pacific Rim' },
        ];

        const feedstockConfig = {
          benzene: {
            label: 'Benzene Imports by Region',
            prefix: 'bz',
            datasets: regions.map(r => ({
              label: r.label,
              data: sorted.map(d => { const v = d[`chart_bz_${r.suffix}`]; return v !== null && v !== undefined ? Number(v) : 0; }),
            })),
          },
          butadiene: {
            label: 'Butadiene Imports by Region',
            prefix: 'bd',
            datasets: regions.map(r => ({
              label: r.label,
              data: sorted.map(d => { const v = d[`chart_bd_${r.suffix}`]; return v !== null && v !== undefined ? Number(v) : 0; }),
            })),
          },
          styrene: {
            label: 'Styrene Exports by Region',
            prefix: 'sm',
            datasets: regions.map(r => ({
              label: r.label,
              data: sorted.map(d => { const v = d[`chart_sm_${r.suffix}`]; return v !== null && v !== undefined ? Number(v) : 0; }),
            })),
          },
        };

        const current = feedstockConfig[selectedFeedstock];

        return (
          <div className={`flex flex-col gap-6 ${isSmallFeedstocksUsitc ? 'p-0 -mx-6' : ''}`}>
            <div className={`border border-rs-jet-light rounded-lg ${isSmallFeedstocksUsitc ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <div className={`flex flex-wrap gap-1.5 mb-3 ${isSmallFeedstocksUsitc ? 'justify-center px-4 pt-4' : ''}`}>
                <button
                  onClick={() => setSelectedFeedstock('benzene')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    selectedFeedstock === 'benzene'
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  Benzene
                </button>
                <button
                  onClick={() => setSelectedFeedstock('butadiene')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    selectedFeedstock === 'butadiene'
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  Butadiene
                </button>
                <button
                  onClick={() => setSelectedFeedstock('styrene')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    selectedFeedstock === 'styrene'
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  Styrene
                </button>
              </div>
              <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallFeedstocksUsitc ? 'text-center px-4' : ''}`}>{current.label}</h3>
              {isSmallFeedstocksUsitc && (
                <div className="flex justify-center mb-3 px-4">
                  <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 w-fit">
                    {current.datasets.map((ds, i) => (
                      <div key={ds.label} className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: chartColors.getColorByIndex(i) }}
                        />
                        <span className="text-rs-off-white text-xs whitespace-nowrap">{ds.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="h-80">
                <StackedBarChart datasets={current.datasets} labels={prodLabels} showDataLabels={false} className="w-full h-full" hideLegend={isSmallFeedstocksUsitc} />
              </div>
            </div>
            <p className={`text-rs-highlight-gray text-xs ${isSmallFeedstocksUsitc ? 'px-4' : ''}`}>Source: American Chemistry Council® (ACC)</p>
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <Loader2 className="animate-spin opacity-30 w-16 h-16 text-rs-green" />
      </div>
    );
  }

  const renderContent = (activeTab: string) => {
    const blurredTabs = ['totals', 'inventory-change', 'demand-production', 'rates', 'feedstocks-usitc'];
    return (
      <FreeTrialFade enabled={blurredTabs.includes(activeTab)}>
        {renderTabContent(activeTab)}
      </FreeTrialFade>
    );
  };

  return (
    <PolymerDashboardLayout config={PS_CONFIG} renderContent={renderContent} />
  );
};

export default PSDashboard;